import { useCenterStore } from '@caradasstore/CenterStore'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { useMobileToolbarStore } from '@caradasstore/MobileToolbarStore'
import { useUserStore } from '@caradasstore/UserStore'
import { ItemPerPageSelector } from '@components/common'
import { EmailForm } from '@components/email'
import { AssigneeAvatarPicker } from '@components/assigned-user'
import { Checkbox } from '@components/ui/checkbox'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@components/ui/table'
import { QueueCard } from '@components/queue-card'
import { QueueSelectionRibbon } from '@components/queue-selection-ribbon'
import { MobileFilterDrawer } from '@components/mobile-filter-drawer'
import { SortableTableHead, nextSort, sortFromQuery, sortRequestParams, writeSortToQuery } from '@components/table-sort'
import { AdasBox, AdasPagination, AdasPaper, AdasStack } from '@components/wrapper-components'
import { AdasCircularProgress } from '@components/wrapper-components'
import { Download, Mail, Plus, Search, Sparkles, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { getWorkorderEmailPreview, getWorkOrderList, getWorkorderPdf } from '../../../api/api'
import { REDIRECT_URLS, getWorkOrderSummaryUrl } from '../../../constants'
import { StatusEnum } from '../../../enums/enums'
import { getWorkOrderStatusPill } from '../../../utils/workOrderStatus'
import { scrollPageTop, useScrollTop } from '../../../utils/useScrollTop'
import { currencyFormatter, formatDurationMinutes } from '../../../utils/utils'
import { Logger } from '../../../logger'
import SearchWithSelect from '@components/search-with-select/SearchWithSelect'
import { useSearchFilters } from '@hooks/useSearchFilters'
import { useIsReadOnly } from '@hooks/useIsReadOnly'

// Sortable columns (keys whitelisted by the backend, QwiListSort). The Work Order column sorts by WO #.
const WORK_ORDER_SORT_OPTIONS = [
  { value: 'referenceNumber', label: 'Work Order #' },
  { value: 'client', label: 'Client' },
  { value: 'assignee', label: 'Assignee' },
  { value: 'status', label: 'Status' },
  { value: 'amount', label: 'Amount' },
  { value: 'duration', label: 'Duration' },
  { value: 'createdAt', label: 'Created' }
]
const WORK_ORDER_SORT_KEYS = WORK_ORDER_SORT_OPTIONS.map((o) => o.value)

export const WorkOrderPage = () => {
  useScrollTop()
  // URL is the source of truth for pagination / search / search-filter chips
  // and the assignee tab, so that goBack from a WO summary restores the same
  // list view and a shared link reproduces it.
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const initialPageNo = Math.max(0, Number(queryParams.get('page') || 0))
  const initialPageSize = Math.max(1, Number(queryParams.get('size') || 25))
  const initialSearch = queryParams.get('q') || ''
  const initialPrimarySearchBy = queryParams.get('psb') || 'ro'
  const initialPrimaryQuery = queryParams.get('pq') || ''
  const initialSecondarySearchBy = queryParams.get('ssb') || 'vin'
  const initialSecondaryQuery = queryParams.get('sq') || ''
  const parseJsonParam = (raw) => {
    if (!raw) return {}
    try {
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
      return {}
    }
  }
  const initialMultiFilters = parseJsonParam(queryParams.get('mf'))
  const initialAssigneeFilter = parseJsonParam(queryParams.get('af'))
  // Column sort (`?sort=&dir=`); the old `?asort=` assignee-only links still open sorted by assignee.
  const legacyAssigneeSort = queryParams.get('asort')
  const initialSort =
    sortFromQuery(queryParams, WORK_ORDER_SORT_KEYS) ||
    (legacyAssigneeSort === 'asc' || legacyAssigneeSort === 'desc' ? { key: 'assignee', dir: legacyAssigneeSort } : null)

  const [workOrderList, setWorkOrderList] = useState([])
  // CAR-3677 — multi-select. `selected` is a Map(id -> work order) driving the
  // ribbon off `selected.size`; single-record actions read the one entry.
  const [selected, setSelected] = useState(() => new Map())
  const singleWorkorder = selected.size === 1 ? Array.from(selected.values())[0] : null
  const [pageParams, setPageParams] = useState({
    pageNo: initialPageNo,
    totalPages: 0,
    totalElements: 0,
    pageSize: initialPageSize,
    search: initialSearch
  })
  const [isEmail, setIsEmail] = useState(false)
  const [emailContacts, setEmailContacts] = useState({})
  const [workorderPdf, setWorkorderPdf] = useState()
  const [searchInput, setSearchInput] = useState(initialSearch)
  const [pdfLoader, setPdfLoader] = useState(false)
  const setLoading = useLoadingStore((store) => store.setLoading)
  const ccId = useCenterStore((store) => store.ccId)
  const user = useUserStore((store) => store.currentUser)

  // Assignee column — mobile tabs filter (server-side via assignedTo)
  const [assigneeFilter, setAssigneeFilter] = useState(initialAssigneeFilter.mode ? initialAssigneeFilter : { mode: 'all', userId: null })
  const [sort, setSort] = useState(initialSort)
  const apiAssignedTo = assigneeFilter.mode === 'me' ? user?.id || '' : assigneeFilter.mode === 'user' ? assigneeFilter.userId || '' : ''
  // Switching the assignee tab resets every active filter (free-text search +
  // search-by chips) so the new tab starts from a clean, unfiltered list.
  const applyAssigneeFilter = (next) => {
    setAssigneeFilter(next)
    setSearchInput('')
    resetSearchFilters()
    setPageParams((prev) => ({ ...prev, pageNo: 0, search: '' }))
  }
  // A new sort starts from the first page; it survives tab and filter changes. Sorted by the server
  // across every page (the old assignee sort only reordered the current page).
  const applySort = (nextValue) => {
    setSort(nextValue)
    setPageParams((prev) => ({ ...prev, pageNo: 0 }))
  }
  const handleSort = (key) => applySort(nextSort(sort, key))
  const history = useHistory()
  const {
    primarySearchBy,
    setPrimarySearchBy,
    primaryQuery,
    setPrimaryQuery,
    secondarySearchBy,
    setSecondarySearchBy,
    secondaryQuery,
    setSecondaryQuery,
    multiFilters,
    setMultiFilters,
    searchFilters,
    searchOptions,
    resetSearchFilters
  } = useSearchFilters({
    identifierValue: 'workorder',
    identifierLabel: 'WO #',
    initialValues: {
      primarySearchBy: initialPrimarySearchBy,
      primaryQuery: initialPrimaryQuery,
      secondarySearchBy: initialSecondarySearchBy,
      secondaryQuery: initialSecondaryQuery,
      multiFilters: initialMultiFilters
    },
    onFiltersChange: () =>
      setPageParams((prev) => ({
        ...prev,
        pageNo: 0
      }))
  })

  const searchOpen = useMobileToolbarStore((s) => s.searchOpen)
  const filterOpen = useMobileToolbarStore((s) => s.filterOpen)
  const closeMobileToolbar = useMobileToolbarStore((s) => s.closeAll)
  useEffect(() => () => closeMobileToolbar(), [closeMobileToolbar])

  // Mirror the search box into the fetch params instantly (no debounce,
  // matching the Invoices queue search behavior).
  useEffect(() => {
    setPageParams((prev) => ({ ...prev, search: searchInput }))
  }, [searchInput])

  // Mirror state into the URL so history.goBack from a WO summary restores the
  // exact list view, and a shared link reproduces it.
  useEffect(() => {
    const params = new URLSearchParams()
    if (pageParams.pageNo > 0) params.set('page', String(pageParams.pageNo))
    if (pageParams.pageSize && pageParams.pageSize !== 25) params.set('size', String(pageParams.pageSize))
    if (pageParams.search) params.set('q', pageParams.search)
    if (primarySearchBy && primarySearchBy !== 'ro') params.set('psb', primarySearchBy)
    if (primaryQuery) params.set('pq', primaryQuery)
    if (secondarySearchBy && secondarySearchBy !== 'vin') params.set('ssb', secondarySearchBy)
    if (secondaryQuery) params.set('sq', secondaryQuery)
    const activeMulti = Object.fromEntries(Object.entries(multiFilters || {}).filter(([, v]) => v != null && v !== ''))
    if (Object.keys(activeMulti).length > 0) params.set('mf', JSON.stringify(activeMulti))
    if (assigneeFilter && assigneeFilter.mode && assigneeFilter.mode !== 'all') params.set('af', JSON.stringify(assigneeFilter))
    writeSortToQuery(params, sort)
    const next = params.toString()
    const current = location.search.replace(/^\?/, '')
    if (next !== current) {
      history.replace({ pathname: window.location.pathname, search: next ? `?${next}` : '' })
    }
  }, [
    pageParams.pageNo,
    pageParams.pageSize,
    pageParams.search,
    primarySearchBy,
    primaryQuery,
    secondarySearchBy,
    secondaryQuery,
    multiFilters,
    assigneeFilter,
    sort,
    history,
    location.search
  ])

  // Client users read the queue; the create CTAs are not offered to them and the
  // /workorders/new route turns them away anyway.
  const isReadOnly = useIsReadOnly()

  const redirectToNewWorkOrder = () => {
    history.push(REDIRECT_URLS.NEW_WORK_ORDER)
    scrollPageTop()
  }

  const handleClick = (data) => {
    history.push(getWorkOrderSummaryUrl(data?.reference_number))
  }

  const paginationHandler = useCallback((event, page) => {
    setPageParams((prev) => ({ ...prev, pageNo: page - 1 }))
  }, [])

  const pageSizeHandler = useCallback((event) => {
    setPageParams((prev) => ({ ...prev, pageSize: event.target.value, pageNo: 0 }))
  }, [])

  useEffect(() => {
    const fetchWorkOrderList = async () => {
      setLoading(true)
      try {
        const response = await getWorkOrderList({
          pageNo: pageParams.pageNo,
          pageSize: pageParams.pageSize,
          ccid: ccId,
          assignedTo: apiAssignedTo,
          search: pageParams.search,
          searchFilters,
          ...sortRequestParams(sort)
        })
        if (response?.status === 200) {
          setWorkOrderList(response.data.workOrders)
          setPageParams((prev) => ({
            ...prev,
            totalPages: response.data.totalPages,
            totalElements: response.data.totalElements ?? response.data.workOrders?.length ?? 0
          }))
          scrollPageTop()
        }
      } catch (error) {
        Logger.error({ message: error, payload: { pageParams, ccId } })
      } finally {
        setLoading(false)
      }
    }
    fetchWorkOrderList()
  }, [pageParams.pageNo, pageParams.pageSize, pageParams.search, searchFilters, sort, ccId, apiAssignedTo, setLoading])

  const displayedWorkOrders = workOrderList || []

  const emailWorkorderHandler = useCallback(async () => {
    if (!singleWorkorder) return
    try {
      const emailContactResponse = await getWorkorderEmailPreview(singleWorkorder.id)
      if (emailContactResponse.status === 200) {
        setIsEmail(true)
        setEmailContacts(emailContactResponse.data)
      }
      const workorderPdfResponse = await getWorkorderPdf(singleWorkorder.id)
      if (workorderPdfResponse.status === 200) {
        const fileValue = new File([workorderPdfResponse.data], `workorder_${singleWorkorder.reference_number}.pdf`)
        fileValue.id = singleWorkorder.id
        setWorkorderPdf(fileValue)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { WorkorderId: singleWorkorder.id, path: 'WorkOrderPage/emailWorkorderHandler' } })
    }
  }, [singleWorkorder])

  const viewPdfHandler = async () => {
    if (!singleWorkorder) return
    try {
      setPdfLoader(true)
      const workorderPdfResponse = await getWorkorderPdf(singleWorkorder.id)
      if (workorderPdfResponse.status === 200) {
        const fileValue = new Blob([workorderPdfResponse.data], { type: 'application/pdf' }, `wo_${singleWorkorder.reference_number}.pdf`)
        fileValue.id = singleWorkorder.id
        const fileURL = URL.createObjectURL(fileValue)
        const pdfWindow = window.open()
        if (pdfWindow === null) {
          alert('Pop-up was blocked by your browser! Please allow pop-ups for caradasplus.com to view the work order.')
        } else {
          pdfWindow.location.href = fileURL
        }
      }
    } catch (error) {
      Logger.error({ message: error, payload: { workorderId: singleWorkorder.id, path: 'WorkOrderPage/viewPdfHandler' } })
    } finally {
      setPdfLoader(false)
    }
  }

  const toggleSelect = (item) => {
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(item.id)) next.delete(item.id)
      else next.set(item.id, item)
      return next
    })
  }

  const clearSelection = () => setSelected(new Map())

  return (
    <div className='mx-auto max-w-[1400px] py-6'>
      <EmailForm
        emailType='workorder'
        workOrderPdf={workorderPdf}
        isOpen={isEmail}
        contacts={emailContacts}
        emailData={{ id: singleWorkorder?.id }}
        onClose={() => setIsEmail(false)}
      />

      {/* Page heading + top-right create CTA. Per CAR-3677 record-level actions
          (View PDF / Email / Close) now live in the selection ribbon; only the
          page-level "New Work Order" create stays here. */}
      <AdasBox className='hidden min-[435px]:flex pt-6 md:pt-4 mb-6 flex-wrap items-center justify-between gap-3'>
        <h1 className='text-[32px] font-bold leading-tight tracking-tight text-foreground'>Work Orders</h1>
        <AdasBox className='hidden md:flex items-center gap-3'>
          {!isReadOnly && (
            <button
              onClick={redirectToNewWorkOrder}
              className='inline-flex items-center gap-1.5 px-5 py-2.5 bg-sidebar text-white text-sm font-medium rounded-lg hover:bg-sidebar/90 transition-colors'
            >
              <Plus className='h-4 w-4' />
              New Work Order
            </button>
          )}
        </AdasBox>
      </AdasBox>

      {/* Mobile inline search bar */}
      {searchOpen && (
        <AdasBox className='min-[435px]:hidden mb-3 mt-2 flex items-center gap-2 rounded-lg bg-[#F4F6F9] px-3 py-2.5'>
          <Search className='h-4 w-4 text-[#6B7280]' />
          <input
            type='text'
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            autoFocus
            placeholder='Client, VIN, RO, vehicle, WO #'
            className='flex-1 border-0 bg-transparent text-[14px] text-[#0A1628] placeholder:text-[#6B7280] focus:outline-none'
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              aria-label='Clear search'
              className='inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-[#475569]'
            >
              <X className='h-3 w-3' />
            </button>
          )}
        </AdasBox>
      )}

      {/* Assignee tabs — All / Assigned to me */}
      <AdasBox className='mb-6 flex items-center gap-7 border-b border-[#D1D5D8]'>
        {[
          { value: 'all', label: 'All' },
          { value: 'me', label: 'Assigned to me' }
        ].map(({ value, label }) => {
          const active = assigneeFilter.mode === value
          return (
            <button
              key={value}
              type='button'
              onClick={() => applyAssigneeFilter({ mode: value, userId: null })}
              className={`pb-3 -mb-px flex items-center gap-2 text-[14px] font-medium transition-colors ${
                active ? 'text-[#1F6FEB] border-b-2 border-[#1F6FEB]' : 'text-[#6B7280] hover:text-[#475569]'
              }`}
            >
              {label}
              {active && pageParams.totalElements > 0 && (
                <span className='inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#DBEAFE] text-[#1F6FEB] text-[11px] font-semibold'>
                  {pageParams.totalElements}
                </span>
              )}
            </button>
          )
        })}
      </AdasBox>

      {/* Filter row */}
      <AdasBox className='mb-5 hidden min-[435px]:flex flex-col md:flex-row items-start md:items-center gap-3'>
        <SearchWithSelect
          selectValue={primarySearchBy}
          onSelectChange={(e) => setPrimarySearchBy(e.target.value)}
          searchValue={primaryQuery}
          onSearchChange={(e) => setPrimaryQuery(e.target.value)}
          options={searchOptions}
          placeholder='Search...'
        />
        <SearchWithSelect
          selectValue={secondarySearchBy}
          onSelectChange={(e) => setSecondarySearchBy(e.target.value)}
          searchValue={secondaryQuery}
          onSearchChange={(e) => setSecondaryQuery(e.target.value)}
          options={searchOptions}
          placeholder='Search...'
        />
      </AdasBox>

      {/* Mobile selection action bar — PDF / Email show only for a single
          selection; Clear is always available (CAR-3677). Work-order bulk close
          has no BE endpoint yet, so Close is omitted here. */}
      {selected.size > 0 && (
        <div
          className='min-[435px]:hidden -mx-3 mb-3 flex items-center justify-between gap-3 px-4 py-3 text-white'
          style={{ background: 'var(--primary-gradient)' }}
        >
          <span className='text-[14px] font-semibold'>{selected.size} selected</span>
          <div className='flex items-center gap-2'>
            {singleWorkorder && (
              <>
                <button
                  onClick={viewPdfHandler}
                  disabled={pdfLoader}
                  className='inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-[13px] font-semibold text-[#0A1628] transition-colors hover:bg-[#F8FAFC] disabled:opacity-40'
                >
                  {pdfLoader ? <AdasCircularProgress color='inherit' size={14} /> : <Download className='h-3.5 w-3.5' />}
                  PDF
                </button>
                {!isReadOnly && (
                  <button
                    onClick={emailWorkorderHandler}
                    className='inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-[13px] font-semibold text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
                  >
                    <Mail className='h-3.5 w-3.5' />
                    Email
                  </button>
                )}
              </>
            )}
            <button onClick={clearSelection} className='text-[13px] font-semibold text-white/80 hover:text-white'>
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Mobile card list */}
      <div className='min-[435px]:hidden pt-2 space-y-3'>
        {displayedWorkOrders.length === 0 ? (
          <div className='rounded-xl border border-[#E5E7EB] bg-white p-8 text-center text-[13px] text-[#6B7280]'>No work orders found</div>
        ) : (
          displayedWorkOrders.map((item) => (
            <QueueCard
              key={item.id}
              item={item}
              itemType='WO'
              isSelected={selected.has(item.id)}
              onSelect={toggleSelect}
              onClick={handleClick}
              assigneeType='work-orders'
            />
          ))
        )}
      </div>

      {/* Desktop selection ribbon — sticky to the top of the scroll area as the
          table scrolls. Rendered as a direct child of the page root (not wrapped)
          so the sticky element has room to travel. No New Appointment (quote-only)
          and no Close yet (work orders have no bulk-close BE endpoint; see CAR-3679). */}
      <QueueSelectionRibbon
        className='hidden min-[435px]:flex'
        count={selected.size}
        recordLabelPlural='Work Orders'
        showClose={false}
        onViewPdf={viewPdfHandler}
        onEmail={emailWorkorderHandler}
        onClear={clearSelection}
        pdfLoading={pdfLoader}
      />

      {/* Work orders table */}
      <div className='hidden min-[435px]:block overflow-hidden rounded-lg border border-[#D1D5D8] bg-white'>
        <Table>
          <TableHeader>
            <TableRow className='bg-gradient-to-r from-[#2C3D4D] to-[#1F2D3D] hover:from-[#2C3D4D] hover:to-[#1F2D3D]'>
              <TableHead className='w-12 text-white' aria-label='Selection' />
              <SortableTableHead
                label='Work Order'
                sortKey='referenceNumber'
                sort={sort}
                onSort={handleSort}
                className='text-white text-[12px] font-semibold uppercase tracking-wide'
              />
              <SortableTableHead
                label='Client'
                sortKey='client'
                sort={sort}
                onSort={handleSort}
                className='text-white text-[12px] font-semibold uppercase tracking-wide'
              />
              <SortableTableHead
                label='Assignee'
                sortKey='assignee'
                sort={sort}
                onSort={handleSort}
                className='text-white text-[12px] font-semibold uppercase tracking-wide'
              />
              <SortableTableHead
                label='Status'
                sortKey='status'
                sort={sort}
                onSort={handleSort}
                className='text-white text-[12px] font-semibold uppercase tracking-wide'
              />
              <SortableTableHead
                label='Amount'
                sortKey='amount'
                sort={sort}
                onSort={handleSort}
                className='text-white text-[12px] font-semibold uppercase tracking-wide'
              />
              <SortableTableHead
                label='Duration'
                title='Time between the pre-calibration and post-calibration odometer readings'
                sortKey='duration'
                sort={sort}
                onSort={handleSort}
                className='text-white text-[12px] font-semibold uppercase tracking-wide'
              />
              <SortableTableHead
                label='Created'
                sortKey='createdAt'
                sort={sort}
                onSort={handleSort}
                className='text-white text-[12px] font-semibold uppercase tracking-wide'
              />
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayedWorkOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className='py-10 text-center text-[13px] text-[#6B7280]'>
                  No work orders found
                  {pageParams.totalPages > 1 && ' on this page. Go to next page to search more work orders.'}
                </TableCell>
              </TableRow>
            ) : (
              displayedWorkOrders.map((item) => {
                const statusPill = getWorkOrderStatusPill(item)
                const isSelected = selected.has(item.id)
                return (
                  <TableRow
                    key={item.id}
                    data-state={isSelected ? 'selected' : undefined}
                    onClick={() => handleClick(item)}
                    className='cursor-pointer h-[72px] even:bg-[#F8FAFC] hover:bg-[#F0F4FF] data-[state=selected]:bg-[#E8EFFF] data-[state=selected]:hover:bg-[#E8EFFF]'
                  >
                    <TableCell className='align-middle' onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleSelect(item)}
                        aria-label={`Select work order ${item.reference_number}`}
                      />
                    </TableCell>
                    <TableCell className='align-middle'>
                      <div className='flex items-start justify-between gap-3'>
                        <div className='flex-1 min-w-0'>
                          <div className='flex flex-wrap items-center gap-x-1.5 text-[12px] text-[#6B7280]'>
                            <span>WO #{item.reference_number}</span>
                            {item.repair_order_num && (
                              <>
                                <span className='text-[#CBD5E1]'>|</span>
                                <span>RO #{item.repair_order_num}</span>
                              </>
                            )}
                            {item.type && (
                              <>
                                <span className='text-[#CBD5E1]'>|</span>
                                <span>{item.type === 'SERVICE_ONLY' ? 'Service' : item.type === 'STANDARD' ? 'Standard' : item.type}</span>
                              </>
                            )}
                            {item.insurance_company?.name && (
                              <>
                                <span className='text-[#CBD5E1]'>|</span>
                                <span>{item.insurance_company.name}</span>
                              </>
                            )}
                          </div>
                          <div className='mt-1 text-[15px] font-bold uppercase tracking-tight text-[#0A1628]'>
                            {[item.vehicle?.year, item.vehicle?.make, item.vehicle?.model].filter(Boolean).join(' ') || '—'}
                          </div>
                          {item.vehicle?.vin && (
                            <div className='mt-0.5 font-mono text-[11px] text-[#6B7280] tracking-wider'>{item.vehicle.vin}</div>
                          )}
                        </div>
                        {item.ai_processed && (
                          <span className='shrink-0 inline-flex h-6 items-center gap-1 rounded-full bg-[#EBEDF0] px-2 text-[10px] font-semibold text-[#475569]'>
                            <Sparkles className='h-3 w-3' />
                            Ai
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className='align-middle text-[13px] text-[#0A1628]'>{item.client?.name || '—'}</TableCell>
                    <TableCell className='align-middle' onClick={(e) => e.stopPropagation()}>
                      <AssigneeAvatarPicker
                        type='work-orders'
                        workOrderId={item.id}
                        initialAssignee={item.assigned_to}
                        closedStatus={StatusEnum[item.status] === StatusEnum.CLOSED}
                      />
                    </TableCell>
                    <TableCell className='align-middle'>
                      <span
                        className={`inline-flex items-center gap-1.5 whitespace-nowrap px-2.5 py-1 rounded-full text-[11px] font-bold uppercase ${statusPill.pill}`}
                      >
                        <span className={`inline-block h-1.5 w-1.5 rounded-full ${statusPill.dot}`} />
                        {statusPill.label}
                      </span>
                    </TableCell>
                    <TableCell className='align-middle text-[15px] font-bold text-[#0A1628]'>
                      {currencyFormatter(item.pre_rebate_total ?? item.total_cost)}
                    </TableCell>
                    <TableCell className='align-middle text-[13px] text-[#0A1628]'>
                      {formatDurationMinutes(item.odo_duration_minutes) || '—'}
                    </TableCell>
                    <TableCell className='align-middle text-[13px] text-[#6B7280]'>
                      {item.created_at
                        ? (() => {
                            const d = new Date(item.created_at)
                            const date = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                            const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
                            return `${date} ${time}`
                          })()
                        : ''}
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile floating "+ New Work Order" CTA */}
      {!isReadOnly && (
        <button
          onClick={redirectToNewWorkOrder}
          className='min-[435px]:hidden fixed bottom-5 right-5 z-30 inline-flex items-center gap-1.5 rounded-full bg-sidebar px-5 py-3 text-sm font-semibold text-white shadow-lg hover:bg-sidebar/90'
        >
          <Plus className='h-4 w-4' />
          New WO
        </button>
      )}

      {/* Mobile filter bottom drawer — primary + secondary SearchWithSelect (matches desktop) */}
      <MobileFilterDrawer
        open={filterOpen}
        onClose={closeMobileToolbar}
        primarySearchBy={primarySearchBy}
        setPrimarySearchBy={setPrimarySearchBy}
        primaryQuery={primaryQuery}
        setPrimaryQuery={setPrimaryQuery}
        secondarySearchBy={secondarySearchBy}
        setSecondarySearchBy={setSecondarySearchBy}
        secondaryQuery={secondaryQuery}
        setSecondaryQuery={setSecondaryQuery}
        searchOptions={searchOptions}
        onClear={() => setMultiFilters({})}
        sortOptions={WORK_ORDER_SORT_OPTIONS}
        sort={sort}
        onSortChange={applySort}
      />

      <AdasPaper elevation={0} className='hidden min-[435px]:flex items-center justify-between p-[20px] mt-[20px]'>
        <AdasBox className='w-[20%]'>
          <ItemPerPageSelector itemPerPageList={[25, 50, 100, 200]} pageSizeHandler={pageSizeHandler} pageSize={pageParams.pageSize} />
        </AdasBox>
        <AdasBox>
          <AdasStack spacing={2}>
            <AdasPagination onChange={paginationHandler} page={pageParams.pageNo + 1} count={pageParams.totalPages} shape='rounded' />
          </AdasStack>
        </AdasBox>
      </AdasPaper>
    </div>
  )
}
